import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { MatchAnalysis } from '../../utils/matchAnalysis';
import ClassifierChart from './ClassifierChart';
import DistributionChart from './DistributionChart';
import MarketTiles from './MarketTiles';
import MatchHero from './MatchHero';
import MatchPicker from './MatchPicker';
import TeamLogo, { teamAbbr } from './TeamLogo';
import TotalsLadder from './TotalsLadder';

interface MatchDashboardProps {
  matches: MatchAnalysis[];
  league: string;
  selected: number;
  onSelect: (index: number) => void;
}

const MatchDashboard: React.FC<MatchDashboardProps> = ({ matches, league, selected, onSelect }) => {
  const { t } = useTranslation();
  const d = t.matchDashboard;
  const match = matches[selected];
  if (!match) return null;

  const teamTitle = (team: string) => (
    <>
      <TeamLogo team={team} league={league} size="sm" />
      {team}
    </>
  );

  return (
    <div className="match-dashboard">
      {matches.length > 1 && (
        <MatchPicker matches={matches} league={league} selected={selected} onSelect={onSelect} />
      )}
      <MatchHero match={match} league={league} />
      <div className="match-dashboard__grid">
        {match.homeGoals.length > 0 && (
          <DistributionChart title={teamTitle(match.home)} caption={d.goals} buckets={match.homeGoals} />
        )}
        {match.awayGoals.length > 0 && (
          <DistributionChart title={teamTitle(match.away)} caption={d.goals} buckets={match.awayGoals} />
        )}
        {match.totalGoals.length > 0 && (
          <DistributionChart title={d.totalGoals} caption={d.distribution} buckets={match.totalGoals} />
        )}
        <TotalsLadder totals={match.totals} />
        <MarketTiles match={match} league={league} />
        {match.classifiers && (
          <ClassifierChart
            rows={match.classifiers.rows}
            ensemble={match.classifiers.ensemble}
            homeAbbr={teamAbbr(match.home, league)}
            awayAbbr={teamAbbr(match.away, league)}
          />
        )}
      </div>
    </div>
  );
};

export default MatchDashboard;
